import { anyspendService } from "../../../anyspend/services/anyspend.js";
import { buildMetadata, buildPayload, normalizeAddress } from "../../../anyspend/utils/index.js";
import { useB3Config } from "../../../global-account/react/index.js";
import { useVisitorData } from "@fingerprintjs/fingerprintjs-pro-react";
import { useMutation } from "@tanstack/react-query";
import { useMemo } from "react";
import { useValidatedClientReferenceId } from "./useValidatedClientReferenceId.js";
/**
 * Hook for creating orders in the Anyspend protocol
 * Handles regular order creation for swaps, NFT minting, tournament participation, etc.
 * For onramp orders, use useAnyspendCreateOnrampOrder instead.
 */
export function useAnyspendCreateOrder({
  onSuccess,
  onError
} = {}) {
  // Get B3 context values
  const {
    partnerId
  } = useB3Config();
  // Get validated client reference ID from B3 context
  const createValidatedClientReferenceId = useValidatedClientReferenceId();
  // Get fingerprint data
  const {
    data: fpData
  } = useVisitorData({
    extendedResult: true
  }, {
    immediate: true
  });
  const visitorData = fpData && {
    requestId: fpData.requestId,
    visitorId: fpData.visitorId
  };
  const {
    mutate: createOrder,
    isPending
  } = useMutation({
    mutationFn: async params => {
      const {
        recipientAddress,
        orderType,
        srcChain,
        dstChain,
        srcToken,
        dstToken,
        srcAmount,
        creatorAddress
      } = params;
      const clientReferenceId = await createValidatedClientReferenceId(params);
      try {
        return await anyspendService.createOrder({
          recipientAddress: normalizeAddress(recipientAddress),
          type: orderType,
          srcChain,
          dstChain,
          srcTokenAddress: normalizeAddress(srcToken.address),
          dstTokenAddress: normalizeAddress(dstToken.address),
          srcAmount: srcAmount,
          payload: buildPayload(orderType, {
            orderType,
            srcToken,
            dstToken,
            expectedDstAmount: params.expectedDstAmount,
            nft: params.nft,
            tournament: params.tournament,
            payload: {
              ...params.payload
            }
          }),
          metadata: buildMetadata(orderType, {
            orderType,
            srcToken,
            dstToken,
            expectedDstAmount: params.expectedDstAmount,
            nft: params.nft,
            tournament: params.tournament,
            payload: {
              ...params.payload
            }
          }),
          creatorAddress: creatorAddress ? normalizeAddress(creatorAddress) : undefined,
          partnerId,
          clientReferenceId,
          visitorData,
          callbackMetadata: params.callbackMetadata
        });
      } catch (error) {
        // If the error has a response with message and statusCode, throw that
        if (error?.data) {
          throw error.data;
        }
        // Otherwise throw the original error
        throw error;
      }
    },
    onSuccess: data => {
      onSuccess?.(data);
    },
    onError: error_0 => {
      onError?.(error_0);
    }
  });
  return useMemo(() => ({
    createOrder,
    isCreatingOrder: isPending
  }), [createOrder, isPending]);
}